import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/ui/components/ui/button";
import { Label } from "@/ui/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/ui/select";
import { useGetAllLocations } from "@/locations/api/queries/location.queries";
import { useGetMachinesByLocation } from "@/machines/api/queries/machines.queries";
import { useCreateExportConfig } from "../../api";
import toast from "react-hot-toast";

interface CreateExportConfigModalProps {
  payload: { organizationId: number };
  onClose: () => void;
}

const CreateExportConfigModal = ({ payload, onClose }: CreateExportConfigModalProps) => {
  const { t } = useTranslation("mlExports");
  const { data: locationsData } = useGetAllLocations();
  const locations = locationsData?.data ?? [];

  const [selectedLocationId, setSelectedLocationId] = useState<number | undefined>();
  const [selectedMachineId, setSelectedMachineId] = useState<number | undefined>();

  const { data: machinesData } = useGetMachinesByLocation(selectedLocationId);
  const machines = machinesData?.data ?? [];

  const createConfig = useCreateExportConfig();

  const handleSubmit = async () => {
    if (!selectedLocationId) return;

    try {
      await createConfig.mutateAsync({
        location_id: selectedLocationId,
        machine_id: selectedMachineId,
      });
      toast.success(t("exportConfigModal.created"));
      onClose();
    } catch {
      toast.error(t("exportConfigModal.createFailed"));
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="space-y-2">
        <Label>{t("exportConfigModal.locationLabel")}</Label>
        <Select
          value={selectedLocationId?.toString() ?? ""}
          onValueChange={(val) => {
            setSelectedLocationId(val ? parseInt(val) : undefined);
            setSelectedMachineId(undefined);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("exportConfigModal.locationPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            {locations.map((loc) => (
              <SelectItem key={loc.id} value={loc.id.toString()}>
                {loc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedLocationId && (
        <div className="space-y-2">
          <Label>{t("exportConfigModal.machineLabel")}</Label>
          <Select
            value={selectedMachineId?.toString() ?? ""}
            onValueChange={(val) => setSelectedMachineId(val ? parseInt(val) : undefined)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("exportConfigModal.machinePlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {machines.map((machine) => (
                <SelectItem key={machine.id} value={machine.id.toString()}>
                  {machine.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>
          {t("exportConfigModal.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!selectedLocationId || createConfig.isPending}
        >
          {createConfig.isPending
            ? t("exportConfigModal.submitting")
            : t("exportConfigModal.submit")}
        </Button>
      </div>
    </div>
  );
};

export default CreateExportConfigModal;
